import { api, get, post } from '@/lib/api';

/**
 * Acesso à gestão de contratos (`/contracts/*`).
 *
 * ## Por que os tipos estão declarados aqui
 *
 * O pacote `@central/contracts` — de onde vêm os tipos das outras telas — está
 * congelado nesta rodada, e é de outro dono. Declarar as interfaces aqui é a
 * alternativa honesta: elas ficam ao lado das chamadas que as produzem, e a
 * cópia do lado do servidor mora em `apps/api/src/itsm/contracts/tipos.ts`,
 * com os mesmos nomes. Quando o pacote reabrir, as duas sobem para lá sem
 * mudar de forma.
 *
 * ## O que **não** existe aqui, e não é esquecimento
 *
 *   • `apagarContrato` — o servidor não tem `DELETE`. Rescindir é
 *     `POST /:id/terminate`, com motivo obrigatório, e não apaga linha alguma;
 *   • `status` em qualquer corpo — a situação é **derivada das datas dos
 *     termos**, e o servidor responde 400 a quem tentar informá-la. No sistema
 *     de origem esse campo é digitado à mão, e 32 dos 63 contratos com situação
 *     dizem "Vigente" já vencidos;
 *   • `ativarTermo` sem lista de substituídos não é um caminho separado: é a
 *     mesma rota sem `supersede`, e ela **recusa** quando há sobreposição. Quem
 *     decide qual termo sai é uma pessoa.
 */

const patch = <T>(caminho: string, corpo: unknown) =>
  api<T>(caminho, { method: 'PATCH', body: corpo });

// ── tipos de fio ───────────────────────────────────────────────────────────

export type ContractDirection = 'INBOUND' | 'OUTBOUND';

export type ContractType =
  | 'MANAGED_SERVICE'
  | 'INFRASTRUCTURE'
  | 'RENTAL'
  | 'PURCHASE'
  | 'MAINTENANCE'
  | 'PROJECT'
  | 'SUPPORT_HOURS'
  | 'WARRANTY'
  | 'UNDERPINNING'
  | 'INTERNAL_OLA'
  | 'PJ_SERVICES'
  | 'SUPPLIER';

export type ContractStatus =
  | 'DRAFT'
  /** Termo já ativado, começando ainda no futuro — não é contrato vencido. */
  | 'SCHEDULED'
  | 'ACTIVE'
  | 'SUSPENDED'
  | 'EXPIRED'
  | 'TERMINATED';
export type TermStatus = 'DRAFT' | 'ACTIVE' | 'SUPERSEDED' | 'EXPIRED';
export type TermOrigin = 'ORIGINAL' | 'RENEWAL' | 'AMENDMENT' | 'REINSTATEMENT';
export type EntitlementKind = 'HOURS' | 'TICKETS';
export type ScopeRuleKind = 'EXPLICIT_CI' | 'BY_SITE' | 'BY_CI_TYPE' | 'BY_TAG' | 'ALL';
export type Coverage = 'COVERED' | 'OUT_OF_TERM' | 'OUT_OF_SCOPE' | 'BILLABLE' | 'NOT_APPLICABLE';

export interface ContractServiceRow {
  id: string;
  serviceId: string;
  serviceName: string;
  slaPolicyId: string | null;
  slaPolicyName: string | null;
  calendarId: string | null;
  visibleInPortal: boolean;
}

export interface AccountCoveredService {
  serviceId: string;
  serviceName: string;
  contracts: Array<{
    contractId: string;
    contractName: string;
    contractNumber: string;
    slaPolicyId: string | null;
    slaPolicyName: string | null;
    visibleInPortal: boolean;
  }>;
}

export interface AccountServicesCovered {
  items: AccountCoveredService[];
}

export interface ContractScopeRow {
  id: string;
  kind: ScopeRuleKind;
  ciId: string | null;
  siteId: string | null;
  ciType: string | null;
  tag: string | null;
  exclude: boolean;
}

export interface ContractTermRow {
  id: string;
  /** Base **0**: o primeiro termo é o zero, como no sistema de origem. */
  sequence: number;
  origin: TermOrigin;
  status: TermStatus;
  externalId: string | null;
  startsAt: string;
  endsAt: string | null;
  autoRenew: boolean;
  autoRenewMonths: number | null;
  noticeDays: number | null;
  monthlyValue: string | null;
  totalValue: string | null;
  paymentFrequency: PaymentFrequency | null;
  commitmentValue: string | null;
  balanceToPay: string | null;
  commitment1stGrade: string | null;
  commitment2ndGrade: string | null;
  entitlementKind: EntitlementKind | null;
  entitlementAmount: number | null;
  entitlementRollover: boolean;
  supersededById: string | null;
  supersededAt: string | null;
  reviewReason: string | null;
  renewalWarnedAt: string | null;
  emVigor: boolean;
  version: number;
  services: ContractServiceRow[];
  scope: ContractScopeRow[];
}

/**
 * A resposta da lista.
 *
 * `truncado` diz que o servidor parou de ler antes do fim da carteira — e,
 * como situação e vencimento são derivados e filtrados **depois** da leitura,
 * uma lista truncada pode estar faltando justamente o contrato que a pessoa
 * procurava. A tela avisa em vez de fingir que a lista é a carteira inteira.
 */
export interface ContractList {
  items: ContractSummary[];
  truncado: boolean;
}

export interface ContractSummary {
  id: string;
  number: string;
  externalNumber: string | null;
  name: string;
  type: ContractType;
  direction: ContractDirection;
  status: ContractStatus;
  accountId: string;
  accountName: string;
  ownerUserId: string | null;
  vigenteDe: string | null;
  vigenteAte: string | null;
  diasParaVencer: number | null;
  termos: number;
  version: number;
}

export interface ContractAmendmentRow {
  id: string;
  number: string;
  signedAt: string;
  summary: string;
  documentUrl: string | null;
  producedTermId: string | null;
  version: number;
}

export type ContractAttachmentKind = 'CONTRATO' | 'EDITAL' | 'PROPOSTA' | 'GARANTIA' | 'ADITIVO' | 'OUTRO';
export type StakeholderSide = 'APTUM' | 'CLIENTE';
export type StakeholderRole = 'PREPOSTO' | 'FISCAL' | 'GESTOR' | 'TECNICO' | 'DEMANDANTE' | 'GESTOR_INTERNO' | 'PRESTADOR';
export type PaymentFrequency = 'MONTHLY' | 'QUARTERLY' | 'SEMIANNUAL' | 'ANNUAL' | 'ONE_TIME';

export interface ContractAttachmentRow {
  id: string;
  kind: ContractAttachmentKind;
  label: string;
  url: string;
  uploadedByUserId: string | null;
  uploadedByName: string | null;
  createdAt: string;
}

export interface ContractStakeholderRow {
  id: string;
  side: StakeholderSide;
  role: StakeholderRole;
  isPrimary: boolean;
  userId: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  createdAt: string;
}

export interface ContractDetail extends ContractSummary {
  organizationId: string | null;
  demandingArea: string | null;
  signedBy: string | null;
  signedOnDate: string | null;
  currency: string;
  renewalWarnDays: number;
  suspendedAt: string | null;
  suspensionReason: string | null;
  terminatedAt: string | null;
  terminationReason: string | null;
  documentUrl: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  terms: ContractTermRow[];
  amendments: ContractAmendmentRow[];
  attachments: ContractAttachmentRow[];
  stakeholders: ContractStakeholderRow[];
}

export interface ContractPanel {
  contractId: string;
  termId: string | null;
  periodoDe: string | null;
  periodoAte: string | null;
  chamados: number;
  chamadosPorCobertura: Record<Coverage, number>;
  minutosApontados: number;
  minutosExcedentes: number;
  entitlementKind: EntitlementKind | null;
  entitlementAmount: number | null;
  consumo: number | null;
  ansAvaliados: number;
  ansCumpridos: number;
}

export interface ContractOverlapRow {
  contractId: string;
  contractNumber: string;
  contractName: string;
  termId: string;
  sequence: number;
  startsAt: string;
  endsAt: string | null;
  reviewReason: string | null;
  colideCom: Array<{ termId: string; sequence: number; startsAt: string; endsAt: string | null }>;
}

// ── entradas ───────────────────────────────────────────────────────────────

export interface FiltroDeContratos {
  q?: string;
  accountId?: string;
  type?: ContractType;
  direction?: ContractDirection;
  status?: ContractStatus;
  /** Contratos cujo termo vigente vence nos próximos N dias. */
  vencendoEm?: number;
}

export interface NovoContrato {
  accountId: string;
  organizationId?: string;
  name: string;
  /** O número do contrato **do cliente** — `TJMT_179_2023`. */
  externalNumber?: string;
  type?: ContractType;
  direction?: ContractDirection;
  signedBy?: string;
  signedOnDate?: string;
  ownerUserId?: string;
  renewalWarnDays?: number;
  documentUrl?: string;
  notes?: string;
  demandingArea?: string;
}

export type AlteracaoDeContrato = Partial<Omit<NovoContrato, 'accountId'>> & { version?: number };

export interface NovoTermo {
  startsAt: string;
  endsAt?: string;
  sequence?: number;
  origin?: TermOrigin;
  externalId?: string;
  autoRenew?: boolean;
  autoRenewMonths?: number;
  noticeDays?: number;
  monthlyValue?: string;
  totalValue?: string;
  paymentFrequency?: PaymentFrequency;
  commitmentValue?: string;
  balanceToPay?: string;
  commitment1stGrade?: string;
  commitment2ndGrade?: string;
  entitlementKind?: EntitlementKind;
  entitlementAmount?: number;
  entitlementRollover?: boolean;
  services?: Array<{
    serviceId: string;
    slaPolicyId?: string;
    calendarId?: string;
    visibleInPortal?: boolean;
  }>;
  scope?: Array<{
    kind: ScopeRuleKind;
    ciId?: string;
    siteId?: string;
    ciType?: string;
    tag?: string;
    exclude?: boolean;
  }>;
}

// ── chamadas ───────────────────────────────────────────────────────────────

export function listarContratos(filtro: FiltroDeContratos = {}) {
  const busca = new URLSearchParams();
  if (filtro.q) busca.set('q', filtro.q);
  if (filtro.accountId) busca.set('accountId', filtro.accountId);
  if (filtro.type) busca.set('type', filtro.type);
  if (filtro.direction) busca.set('direction', filtro.direction);
  if (filtro.status) busca.set('status', filtro.status);
  if (filtro.vencendoEm !== undefined) busca.set('vencendoEm', String(filtro.vencendoEm));
  const consulta = busca.toString();
  return get<ContractList>(`/contracts${consulta ? `?${consulta}` : ''}`);
}

export const obterContrato = (id: string) => get<ContractDetail>(`/contracts/${id}`);

export const obterServicosDoCliente = (accountId: string) =>
  get<AccountServicesCovered>(`/contracts/account-services?accountId=${accountId}`);

export const obterPainel = (id: string) => get<ContractPanel>(`/contracts/${id}/panel`);

export interface PontoDeUso {
  date: string;
  total: number;
  cobertos: number;
}

export interface HistoricoDeUso {
  termId?: string;
  points: PontoDeUso[];
}

export const obterHistoricoDeUso = (contratoId: string) =>
  api<HistoricoDeUso>(`/contracts/${contratoId}/panel/history`);

export const listarSobreposicoes = () =>
  get<{ items: ContractOverlapRow[] }>('/contracts/overlaps');

export const criarContrato = (corpo: NovoContrato) => post<ContractDetail>('/contracts', corpo);

export const alterarContrato = (id: string, corpo: AlteracaoDeContrato) =>
  patch<ContractDetail>(`/contracts/${id}`, corpo);

export const suspenderContrato = (id: string, corpo: { reason: string; version?: number }) =>
  post<ContractDetail>(`/contracts/${id}/suspend`, corpo);

export const retomarContrato = (id: string, corpo: { reason?: string; version?: number }) =>
  post<ContractDetail>(`/contracts/${id}/resume`, corpo);

export const rescindirContrato = (id: string, corpo: { reason: string; version?: number }) =>
  post<ContractDetail>(`/contracts/${id}/terminate`, corpo);

export const criarTermo = (contratoId: string, corpo: NovoTermo) =>
  post<ContractDetail>(`/contracts/${contratoId}/terms`, corpo);

export const alterarTermo = (termoId: string, corpo: Partial<NovoTermo> & { version?: number }) =>
  patch<ContractDetail>(`/contracts/terms/${termoId}`, corpo);

/**
 * Ativa um termo.
 *
 * `supersede` é a lista de termos que este substitui, e ela é **decisão
 * humana**: sem ela, um termo que se sobrepõe a outro em vigor é recusado com
 * a lista dos conflitos na mensagem. É por aqui que os pares sobrepostos
 * herdados do sistema anterior são resolvidos, um a um.
 */
export const ativarTermo = (
  termoId: string,
  corpo: { supersede?: string[]; reason?: string; version?: number } = {},
) => post<ContractDetail>(`/contracts/terms/${termoId}/activate`, corpo);

/** Renovar cria o termo seguinte em **rascunho** — a ativação é sempre manual. */
export const excluirRascunhoDeTermo = (termoId: string) =>
  api<ContractDetail>(`/contracts/terms/${termoId}`, { method: 'DELETE' });

export const renovarTermo = (
  termoId: string,
  corpo: { months?: number; endsAt?: string; copiarCobertura?: boolean } = {},
) => post<ContractDetail>(`/contracts/terms/${termoId}/renew`, corpo);

export const adicionarServicoDeTermo = (
  termoId: string,
  corpo: { serviceId: string; slaPolicyId?: string; visibleInPortal?: boolean },
) => post<ContractDetail>(`/contracts/terms/${termoId}/services`, corpo);

export const removerServicoDeTermo = (termoId: string, serviceId: string) =>
  api<ContractDetail>(`/contracts/terms/${termoId}/services/${serviceId}`, { method: 'DELETE' });

export const criarAditivo = (
  contratoId: string,
  corpo: { number: string; signedAt: string; summary: string; documentUrl?: string; term: NovoTermo },
) => post<ContractDetail>(`/contracts/${contratoId}/amendments`, corpo);

// ── scope rules ───────────────────────────────────────────────────────────────

export interface RegraDeEscopo {
  id: string;
  termId: string;
  kind: ScopeRuleKind;
  ciId?: string | null;
  siteId?: string | null;
  ciType?: string | null;
  tag?: string | null;
  exclude: boolean;
  createdAt: string;
}

export const listarEscopoDoTermo = (contratoId: string, termoId: string) =>
  api<RegraDeEscopo[]>(`/contracts/${contratoId}/terms/${termoId}/scope`);

export const criarRegraDeEscopo = (
  contratoId: string,
  termoId: string,
  data: { kind: string; ciId?: string; siteId?: string; ciType?: string; tag?: string; exclude?: boolean },
) => api<RegraDeEscopo>(`/contracts/${contratoId}/terms/${termoId}/scope`, { method: 'POST', body: data });

export const excluirRegraDeEscopo = (contratoId: string, termoId: string, ruleId: string) =>
  api<{ ok: boolean }>(`/contracts/${contratoId}/terms/${termoId}/scope/${ruleId}`, { method: 'DELETE' });

// ── chaves do TanStack Query ───────────────────────────────────────────────

/**
 * Uma raiz só, e invalidação pela raiz depois de escrever.
 *
 * Ativar um termo muda a situação do contrato na lista, muda o painel e tira
 * uma linha da fila de sobreposições: enumerar cada chave afetada seria uma
 * lista para esquecer de atualizar.
 */
export const chavesDeContratos = {
  raiz: ['contratos'] as const,
  lista: (filtro: FiltroDeContratos = {}) =>
    [
      'contratos',
      'lista',
      filtro.q ?? '',
      filtro.accountId ?? '',
      filtro.type ?? '',
      filtro.status ?? '',
      filtro.vencendoEm ?? '',
    ] as const,
  ficha: (id: string) => ['contratos', 'ficha', id] as const,
  painel: (id: string) => ['contratos', 'painel', id] as const,
  sobreposicoes: ['contratos', 'sobreposicoes'] as const,
  servicosDaConta: (accountId: string) => ['contratos', 'servicos-da-conta', accountId] as const,
};

// ── export ────────────────────────────────────────────────────────────────────
export const exportarContratosCSV = (filtro: FiltroDeContratos) => {
  const params = new URLSearchParams();
  if (filtro.q)             params.set('q', filtro.q);
  if (filtro.accountId)     params.set('accountId', filtro.accountId);
  if (filtro.type)          params.set('type', filtro.type);
  if (filtro.status)        params.set('status', filtro.status);
  if (filtro.vencendoEm)    params.set('vencendoEm', String(filtro.vencendoEm));
  // Return full URL so the browser can use it as an <a href> for download
  return `/api/v1/contracts/export.csv?${params.toString()}`;
};

// ── dashboard ─────────────────────────────────────────────────────────────────
export interface DashboardDeContratos {
  totalAtivos: number;
  totalSuspensos: number;
  totalEncerrados: number;
  totalSemTermo: number;
  mrr: number;
  expirandoEm30: number;
  expirandoEm60: number;
  expirandoEm90: number;
  topContasPorMrr: Array<{ id: string; name: string; mrr: number }>;
}

export const obterDashboardDeContratos = () =>
  get<DashboardDeContratos>('/contracts/dashboard');

// ── Anexos ───────────────────────────────────────────────────────────────────

export interface NovoAnexo {
  kind: ContractAttachmentKind;
  label: string;
  url: string;
}

export const adicionarAnexo = (contratoId: string, corpo: NovoAnexo) =>
  post<ContractDetail>(`/contracts/${contratoId}/attachments`, corpo);

export const removerAnexo = (contratoId: string, attachmentId: string) =>
  api<ContractDetail>(`/contracts/${contratoId}/attachments/${attachmentId}`, { method: 'DELETE' });

// ── Partes (prepostos e fiscais) ──────────────────────────────────────────────

export interface NovaParte {
  side: StakeholderSide;
  role: StakeholderRole;
  isPrimary?: boolean;
  userId?: string;
  name?: string;
  email?: string;
  phone?: string;
  notes?: string;
}

export const adicionarParte = (contratoId: string, corpo: NovaParte) =>
  post<ContractDetail>(`/contracts/${contratoId}/stakeholders`, corpo);

export const removerParte = (contratoId: string, stakeholderId: string) =>
  api<ContractDetail>(`/contracts/${contratoId}/stakeholders/${stakeholderId}`, { method: 'DELETE' });
